import React, { useState, useEffect, useRef, useMemo } from 'react';
import DynamicIcon from './DynamicIcon';
import { useSession } from '../contexts/SessionContext';
import { useBranding } from '../contexts/BrandingContext';
// CIN-6879: one matcher shared with SessionContext's deep-link resolver, so the
// context advance and this step-2 safety net can never disagree.
import { findDeepLinkService } from '../utils/deepLink';
import { formatServiceDuration, formatServicePrice, STARTING_AT_LABEL } from '../utils/serviceDisplay';

const ServiceSelection = () => {
  const { selectedLocation, selectedServices, setSelectedServices, sessionLoading } = useSession();
  const { widgetConfig: config } = useBranding();
  // Memoized so the deep-link effect below has a stable services reference
  // (avoids re-running every render) and to satisfy react-hooks/exhaustive-deps.
  const services = useMemo(() => selectedLocation?.services || [], [selectedLocation]);
  // State for search functionality
  const [searchTerm, setSearchTerm] = useState('');

  // CIN-6441 (P1): honor RwG deep links of the form
  // https://booking.cinch.io/<calendar_id>?service=<service_id>. When the
  // services list has loaded and the user hasn't chosen anything yet, preselect
  // the deep-linked service so it matches a click (Scheduler.isStepValid case 2
  // then enables Next with no interaction).
  // We read window.location.search directly rather than react-router's
  // useLocation: it's the same query string at runtime (the widget mounts under
  // BrowserRouter), but keeps this component out of react-router's import graph
  // so the existing jest suites (which mock the router-using contexts) still run.
  // The session hydration in SessionContext is async and unconditionally
  // rewrites selectedServices when its response lands (resumed-session cookie
  // path), which can clobber an already-applied preselect. So instead of a
  // one-shot latch, the effect re-applies whenever the selection is empty —
  // and permanently stands down the moment the user manually selects or
  // deselects anything (userTouched, set in the click handler below). It also
  // waits for sessionLoading to settle so it runs after hydration, not before.
  const userTouched = useRef(false);
  useEffect(() => {
    if (userTouched.current) return; // user intent always wins
    if (sessionLoading) return; // wait for session hydration to settle
    if (services.length === 0) return; // services not loaded yet
    if (selectedServices.length > 0) return; // resumed choice already present
    // findDeepLinkService reads ?service= and compares ids via String() (feed
    // ids may be numeric). No param or an unknown/invalid id => no-op, so the
    // visitor falls through to the normal picker and is never blocked.
    const match = findDeepLinkService({ services, search: window.location.search });
    if (!match) return;
    setSelectedServices([match]);
  }, [sessionLoading, services, selectedServices, setSelectedServices]);
  // Sort services by sort_order
  const sortedServices = [...services].sort((a, b) => {
    const orderA = a.sort_order ?? 999; // Default high value for undefined sort_order
    const orderB = b.sort_order ?? 999;
    return orderA - orderB;
  });

  // Filter services based on search term
  const filteredServices = sortedServices.filter(service => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      service.name?.toLowerCase().includes(search) ||
      service.description?.toLowerCase().includes(search) ||
      service.type?.toLowerCase().includes(search) ||
      service.category?.toLowerCase().includes(search) ||
      service.price?.toString().includes(search) ||
      formatServiceDuration(service.time_estimate)?.toLowerCase().includes(search)
    );
  });

  if (!services || services.length === 0) {
    return (
      <div className="step-content">
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          color: '#666' 
        }}>
          <p className="body-text">No services available for the selected location.</p>
        </div>
      </div>
    );
  }

  // Check if search should be shown (default true)
  const showSearch = config?.show_search !== false;

  return (
    <div className="step-content">
      {/* Search input */}
      {showSearch && (
        <div className="search-container">
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      <div className="services-list">
        {filteredServices.map(service => {
          const priceText = formatServicePrice(service);
          return (
          <div
            key={service.id}
            className={`service-item ${selectedServices.find(s => s.id === service.id) ? 'selected' : ''}`}
            onClick={() => {
              userTouched.current = true; // manual choice: deep-link preselect stands down
              const isSelected = selectedServices.find(s => s.id === service.id);
              if (isSelected) {
                setSelectedServices(selectedServices.filter(s => s.id !== service.id));
              } else {
                setSelectedServices([...selectedServices, service]);
              }
            }}
            style={{ backgroundColor: 'white' }}
          >
            <div style={{ display: 'flex', alignItems: 'stretch', gap: '16px', height: '100%' }}>
              {/* Icon - takes full height, left-aligned */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '48px',
                flexShrink: 0
              }}>
                {service.icon && (
                  <DynamicIcon
                    iconName={service.icon}
                    size="2x"
                    className="service-icon"
                    style={{ color: 'var(--primary-color)' }}
                  />
                )}
              </div>

              {/* Content - three rows */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
                {/* Row 1: Service Name */}
                <h2 style={{
                  margin: 0,
                  fontWeight: 'bold',
                  color: 'black',
                  fontSize: '18px',
                  lineHeight: '1.2',
                  textAlign: 'left'
                }}>
                  {service.name}
                </h2>

                {/* Row 2: Service Description */}
                {service.description && (
                  <p style={{
                    margin: 0,
                    fontWeight: 'normal',
                    color: 'black',
                    fontSize: '14px',
                    lineHeight: '1.3',
                    textAlign: 'left'
                  }}>
                    {service.description}
                  </p>
                )}

                {/* Row 3: Time and Price */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  fontSize: '13px',
                  lineHeight: '1.2',
                  justifyContent: 'flex-start'
                }}>
                  {service.time_estimate && (
                    <span>
                      <span style={{ fontWeight: '300', color: '#666' }}>Est. Time </span>
                      <span style={{ fontWeight: 'normal', color: 'black' }}>{formatServiceDuration(service.time_estimate)}</span>
                    </span>
                  )}
                  {priceText && (
                    <span data-testid="service-price">
                      <span style={{ fontWeight: '300', color: '#666' }}>{STARTING_AT_LABEL} </span>
                      <span style={{ fontWeight: 'normal', color: 'black' }}>{priceText}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceSelection;